import type { PostSummary } from '@tomato/shared';
import { useEffect, useState } from 'react';

import { api, ApiError } from '../api/client';
import { EmptyState, Notice, PostListSkeleton } from '../components/ui';
import { useRefreshStore } from '../store/refresh';
import { PostRow } from './BoardScreen';

/**
 * "찜한 상품" — 관심 상품을 글에 들어가지 않고도 현재 상태·가격으로 확인한다(SPEC 2-A).
 * status/price는 서버가 posts를 그대로 조인해 내려주므로 항상 최신이다.
 * 완료된 거래는 PostRow가 흐리게 + 취소선으로 그린다.
 */
export function FavoritesScreen() {
  const refreshTick = useRefreshStore((s) => s.tick);
  const [items, setItems] = useState<PostSummary[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    void api
      .get<{ items: PostSummary[] }>('/api/me/favorites')
      .then((res) => !cancelled && setItems(res.items))
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : '찜한 상품을 불러오지 못했어요.');
        setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshTick]); // 당겨서 새로고침이 tick을 올리면 다시 불러온다

  return (
    <div className="py-6">
      <h1 className="mb-5 text-[26px] font-semibold tracking-tight text-ink-900">찜한 상품</h1>

      {items === null ? (
        <PostListSkeleton rows={4} />
      ) : items.length === 0 ? (
        <EmptyState
          icon="❤️"
          title="아직 찜한 상품이 없어요"
          body="장터에서 관심 있는 물건을 찜해두면 상태·가격 변동을 알려드려요."
        />
      ) : (
        <div className="overflow-hidden rounded-2xl bg-surface px-4 shadow-card">
          {items.map((post, i) => (
            <div key={post.id}>
              {i > 0 && <div className="h-px bg-ink-100" />}
              <PostRow post={post} />
            </div>
          ))}
        </div>
      )}

      <Notice>{error}</Notice>
    </div>
  );
}
